/**
 * ThemeService — applies the user's `theme` preference to the document
 * by writing `data-theme="light"` or `data-theme="dark"` on `<html>`.
 *
 * Three resolved modes:
 *  - `'light'`  → `data-theme="light"`. We stamp this explicitly so it
 *                 wins over the CSS `prefers-color-scheme` fallback,
 *                 which would otherwise force dark tokens on a dark OS.
 *  - `'dark'`   → `data-theme="dark"`.
 *  - `'system'` → attribute removed; the CSS
 *                 `:root:not([data-theme])` + `prefers-color-scheme`
 *                 rules in styles.scss take over for the first paint,
 *                 and we listen to `matchMedia` so the page also
 *                 reacts to OS-level toggles while the app is open.
 *
 * Bootstrapped from `app.config.ts` via `provideAppInitializer` so the
 * service starts running before the shell mounts and the very first
 * render is correct.
 */

import { DestroyRef, effect, inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { UserPreferencesService } from '../account/user-preferences.service';
import type { ThemePreference } from '../account/account.types';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly preferences = inject(UserPreferencesService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  /** `matchMedia` handle, lazily created the first time `'system'` resolves. */
  private mql: MediaQueryList | null = null;
  private mqlListener: ((e: MediaQueryListEvent) => void) | null = null;

  constructor() {
    effect(() => {
      const choice = this.preferences.theme();
      this.apply(choice);
    });

    this.destroyRef.onDestroy(() => this.detachSystemListener());
  }

  private apply(choice: ThemePreference): void {
    const root = this.document.documentElement;
    if (choice === 'dark') {
      this.detachSystemListener();
      root.setAttribute('data-theme', 'dark');
      return;
    }
    if (choice === 'light') {
      this.detachSystemListener();
      // Stamp `light` explicitly — just removing the attribute would
      // let the `prefers-color-scheme: dark` CSS fallback re-apply
      // dark tokens on a dark-themed OS.
      root.setAttribute('data-theme', 'light');
      return;
    }
    // 'system' — let CSS handle the first paint via prefers-color-scheme,
    // and listen for OS-level toggles so we update on the fly.
    root.removeAttribute('data-theme');
    this.attachSystemListener();
  }

  private attachSystemListener(): void {
    if (this.mql && this.mqlListener) return;
    const win = this.document.defaultView;
    if (!win || typeof win.matchMedia !== 'function') return;
    this.mql = win.matchMedia('(prefers-color-scheme: dark)');
    this.mqlListener = (e: MediaQueryListEvent) => {
      const root = this.document.documentElement;
      if (e.matches) {
        root.setAttribute('data-theme', 'dark');
      } else {
        root.removeAttribute('data-theme');
      }
    };
    // Older Safari uses addListener — fall back if needed. modern
    // browsers all support addEventListener.
    if (typeof this.mql.addEventListener === 'function') {
      this.mql.addEventListener('change', this.mqlListener);
    } else {
      this.mql.addListener(this.mqlListener);
    }
  }

  private detachSystemListener(): void {
    if (this.mql && this.mqlListener) {
      if (typeof this.mql.removeEventListener === 'function') {
        this.mql.removeEventListener('change', this.mqlListener);
      } else {
        this.mql.removeListener(this.mqlListener);
      }
    }
    this.mql = null;
    this.mqlListener = null;
  }
}
